import { CloudOff, LogIn, RefreshCw } from 'lucide-react';
import type { ProjectCardAvailability, ProjectRef } from '@volley/crucible-ipc-types/ipc-channels';
import type { CloudAccountStatus } from '@volley/crucible-ipc-types/ipc-channels';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

export function projectAccessAfterCloudSignIn(
  status: Exclude<CloudAccountStatus, { state: 'eligible' }>,
): { availability: ProjectCardAvailability; reason?: string } {
  if (status.state === 'unavailable') {
    return {
      availability: 'offline',
      reason: `Cloud account status is unavailable (${status.reason}).`,
    };
  }
  return { availability: status.state };
}

export function projectAccessCopy(
  availability: ProjectCardAvailability,
  reason?: string | null,
): { title: string; detail: string; action: 'sign-in' | 'retry' | 'none' } {
  switch (availability) {
    case 'signed-out':
      return {
        title: 'Sign in to open this Cloud project',
        detail:
          reason ?? 'Foundry will return to this project after your Cloud session is restored.',
        action: 'sign-in',
      };
    case 'offline':
      return {
        title: 'Cloud is offline',
        detail:
          reason ?? 'This cached project is safe, but its build and preview lifecycle need Cloud.',
        action: 'retry',
      };
    case 'ineligible':
      return {
        title: 'Cloud access is unavailable for this account',
        detail: reason ?? 'This project stays in Cloud and will never fall back to This Mac.',
        action: 'none',
      };
    case 'creating':
      return {
        title: 'This Cloud project is still being created',
        detail:
          reason ?? 'Retry after creation finishes. Foundry will not open a partial Local copy.',
        action: 'retry',
      };
    default:
      return {
        title: 'Cloud project unavailable',
        detail: reason ?? 'Retry the Cloud connection to continue.',
        action: 'retry',
      };
  }
}

export function ProjectAccessDialog({
  project,
  availability,
  reason,
  onClose,
  onSignIn,
  onRetry,
}: {
  project: ProjectRef | null;
  availability: ProjectCardAvailability;
  reason?: string | null;
  onClose: () => void;
  onSignIn?: () => void;
  onRetry?: () => void;
}): React.JSX.Element {
  const copy = projectAccessCopy(availability, reason);
  return (
    <Dialog open={project !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>{copy.title}</DialogTitle>
        <div className="flex items-start gap-3 text-sm text-white/60">
          <CloudOff size={18} className="mt-0.5 shrink-0 text-amber-300" />
          <DialogDescription className="text-sm text-white/60">{copy.detail}</DialogDescription>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {copy.action === 'sign-in' && onSignIn && (
            <Button onClick={onSignIn}>
              <LogIn size={14} />
              Sign in
            </Button>
          )}
          {copy.action === 'retry' && onRetry && (
            <Button onClick={onRetry}>
              <RefreshCw size={14} />
              Retry
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
